import React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link, useParams } from 'react-router-dom';

import parking from 'src/assets/images/parking.svg';
import { useGetMembership } from 'src/features/membership/api/useGetMembership';
import DeleteMembershipFormModal from 'src/features/membership/components/forms/DeleteMembershipFormModal';
import UpdateMembershipFormModal from 'src/features/membership/components/forms/UpdateMembershipFormModal';
import { DAY_OF_WEEK } from 'src/features/membershipType/types';
import { membershipMessages } from 'src/features/membership/utils/messages';
import { useGetMembershipType } from 'src/features/membershipType/api/useGetMembershipType';
import BooleanBadge from 'src/features/membership/components/BooleanBadge';
import {
    DURATION_UNIT_PLURAL_TRANSLATION_MAP,
    membershipTypeBadgeOptions,
} from 'src/features/membershipType/utils/constants';
import { DURATION_UNIT } from 'src/features/membershipType/types';
import { PATH } from 'src/features/navigation/utils/constants';
import { Translation } from 'src/types';
import { GeneralBlock, GeneralBlockValue } from 'src/registry/components/blocks/GeneralBlock';
import { SectionBlockTitle } from 'src/registry/components/blocks/SectionBlock';
import { Badge } from 'src/registry/ui/Badge';
import { Button } from 'src/registry/ui/Button';
import { Skeleton } from 'src/registry/ui/Skeleton';
import { ICON_MAP } from 'src/utils/constants';
import { cn } from 'src/utils/shadcn';
import { useTitleStore } from 'src/stores/useTitleStore';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'src/registry/ui/Tooltip';
import { commonMessages } from 'src/utils/messages';
import { generalMessages } from 'src/registry/utils/messages';
import { useParking } from 'src/providers/ParkingProvider';
import GeneralBlockAttribute from 'src/registry/components/blocks/GeneralBlockAttribute';
import { getFormattedDateTimeForDetailPanel } from 'src/utils/getFormattedDateTime';
import { ENTITY_TYPE } from 'src/features/entity/types';
import useHasPermissions from 'src/hooks/useHasPermissions';
import { PERMISSION } from 'src/features/auth';
import { LinkWithIcon } from 'src/registry/components/navigation/LinkWithIcon';
import MembershipDashboardTables from 'src/features/membership/components/MembershipDashboardTables';

dayjs.extend(duration);

enum UNIT_MILLISECONDS {
    HOUR = 60 * 60 * 1000,
    DAY = 24 * UNIT_MILLISECONDS.HOUR,
    WEEK = 7 * UNIT_MILLISECONDS.DAY,
    MONTH = 30 * UNIT_MILLISECONDS.DAY,
    YEAR = 365 * UNIT_MILLISECONDS.DAY,
}
function normalizeDuration(ms: number) {
    let remainingMs = ms;

    const units = [
        { unit: DURATION_UNIT.YEAR, ms: UNIT_MILLISECONDS.YEAR },
        { unit: DURATION_UNIT.MONTH, ms: UNIT_MILLISECONDS.MONTH },
        { unit: DURATION_UNIT.WEEK, ms: UNIT_MILLISECONDS.WEEK },
        { unit: DURATION_UNIT.DAY, ms: UNIT_MILLISECONDS.DAY },
        { unit: DURATION_UNIT.HOUR, ms: UNIT_MILLISECONDS.HOUR },
    ];

    return units.reduce(
        (acc, { unit, ms }) => {
            const value = Math.floor(remainingMs / ms);
            if (value > 0) {
                acc.push({ value, unit });
                remainingMs -= value * ms;
            }
            return acc;
        },
        [] as { unit: DURATION_UNIT; value: number }[],
    );
}

const formatTime = (date: string) => date.split('.')[0];

export default function MembershipDashboard() {
    const { membershipId = '', category = '' } = useParams();
    const { formatMessage, locale } = useIntl();
    const manageMembershipsPermission = useHasPermissions([PERMISSION.MANAGE_MEMBERSHIPS]);
    const accessEntityPermission = useHasPermissions([PERMISSION.ACCESS_ENTITIES]);
    const accessMembershipTemplatePermission = useHasPermissions([PERMISSION.ACCESS_MEMBERSHIP_TYPES]);

    const { parkingId } = useParking();
    const { data, isLoading } = useGetMembership({ parkingId, membershipId });

    const { data: membershipTypeData } = useGetMembershipType({
        parkingId,
        membershipTypeId: data?.membership_type.id || '',
        config: { enabled: !!data?.membership_type.id },
    });
    const { setTitle, removeTitle } = useTitleStore();

    const { duration, customDuration } = React.useMemo(() => {
        const result = {
            duration: '-',
            customDuration: false,
        };
        const validTo = data?.valid_to;
        const validFrom = data?.valid_from;
        const hasTypeDuration = membershipTypeData?.duration && membershipTypeData?.duration_unit;

        if (!validTo && !hasTypeDuration) return result;

        if (hasTypeDuration) {
            result.duration = formatMessage(DURATION_UNIT_PLURAL_TRANSLATION_MAP[membershipTypeData?.duration_unit], {
                value: membershipTypeData?.duration,
            });
        }

        if (!validTo) return result;

        const membershipTypeDuration =
            hasTypeDuration && membershipTypeData
                ? dayjs.duration(membershipTypeData.duration!, membershipTypeData?.duration_unit).asMilliseconds()
                : 0;
        const from = dayjs(validFrom);
        const to = dayjs(validTo);
        const membershipDuration = to.diff(from);
        result.customDuration = membershipDuration !== membershipTypeDuration;

        const durationsArray = normalizeDuration(membershipDuration);
        result.duration = durationsArray
            .map(({ unit, value }) => formatMessage(DURATION_UNIT_PLURAL_TRANSLATION_MAP[unit], { value }))
            .join(' ');
        return result;
    }, [data?.valid_from, data?.valid_to, formatMessage, membershipTypeData]);

    const categoryBadgeOption = React.useMemo(
        () => (data ? membershipTypeBadgeOptions[data?.membership_type.category] : null),
        [data],
    );

    const templateId = data?.membership_type?.id ?? '';

    React.useEffect(() => {
        setTitle(formatMessage(membershipMessages.membershipDashboard));

        return removeTitle;
    }, [formatMessage, removeTitle, setTitle]);

    const translate = React.useCallback(
        (translations?: Translation[]) => {
            if (!translations) return '-';
            const localeTranslation = translations.find((translation) => translation.language === locale)?.name;
            return localeTranslation ?? '-';
        },
        [locale],
    );

    return (
        <>
            <Button asChild className="-mt-6 place-self-start p-0" variant="link">
                <Link to={`/${PATH.MEMBERSHIPS}/${category}`}>
                    <FontAwesomeIcon icon={ICON_MAP.BACK} className="opacity-50" />
                    {formatMessage(membershipMessages.backToMembershipCategories)}
                </Link>
            </Button>
            <div className="flex flex-row items-end justify-between p-0">
                <span className="flex flex-row items-center">
                    <SectionBlockTitle className="mr-2">{data?.name}</SectionBlockTitle>
                    {categoryBadgeOption && (
                        <Badge theme={categoryBadgeOption.theme}>
                            <FontAwesomeIcon icon={categoryBadgeOption.icon!} />
                            {formatMessage(categoryBadgeOption.label)}
                        </Badge>
                    )}
                </span>
                {data && manageMembershipsPermission && (
                    <div className="flex flex-row gap-2">
                        <UpdateMembershipFormModal
                            semantics={{ entryName: formatMessage(membershipMessages.membership).toLowerCase() }}
                            selectedMembership={data}
                        >
                            <Button size="mdIcon" variant="outline" className="cursor-pointer">
                                <FontAwesomeIcon icon={ICON_MAP.EDIT} />
                                <span className="hidden md:block">{formatMessage(commonMessages.edit)}</span>
                            </Button>
                        </UpdateMembershipFormModal>

                        <DeleteMembershipFormModal
                            semantics={{ entryName: formatMessage(membershipMessages.membership).toLowerCase() }}
                            selectedMembership={data}
                        >
                            <Button size="mdIcon" variant="destructiveLight" className="cursor-pointer">
                                <FontAwesomeIcon icon={ICON_MAP.DELETE} />
                                <span className="hidden md:block">{formatMessage(generalMessages.delete)}</span>
                            </Button>
                        </DeleteMembershipFormModal>
                    </div>
                )}
            </div>
            <div className="flex flex-col gap-9">
                <div className="flex gap-9">
                    <GeneralBlock className="flex-1 items-center justify-center">
                        <img src={parking} alt="Parking" />
                    </GeneralBlock>
                    <GeneralBlock className="grid flex-3 grid-cols-3">
                        <div className="flex flex-col justify-between">
                            {isLoading ? (
                                <Skeleton className="size-14 rounded-full" />
                            ) : (
                                <div
                                    className={cn(
                                        'flex size-14 items-center justify-center rounded-full border-8 p-1',
                                        data?.in_parking
                                            ? 'border-green-50 bg-green-100 text-green-700'
                                            : 'border-red-50 bg-red-100 text-red-700',
                                    )}
                                >
                                    <FontAwesomeIcon icon={ICON_MAP.WAVE_PULSE} className="text-2xl" />
                                </div>
                            )}
                            <GeneralBlockAttribute
                                label={formatMessage(membershipMessages.identifierCount, {
                                    count: data?.membership_identifiers_count,
                                })}
                            >
                                <p className="text-xl font-semibold lg:text-3xl">
                                    <FormattedMessage
                                        {...(data?.in_parking
                                            ? membershipMessages.inParking
                                            : membershipMessages.notInParking)}
                                    />
                                </p>
                            </GeneralBlockAttribute>
                        </div>
                        <div className="flex flex-col gap-4">
                            <GeneralBlockAttribute
                                label={formatMessage(membershipMessages.owner)}
                                value={data?.owner?.name}
                                link={
                                    accessEntityPermission && data?.owner?.id
                                        ? `/${PATH.ENTITIES}/${ENTITY_TYPE.PERSON}/${data.owner.id}`
                                        : undefined
                                }
                                icon={ICON_MAP.ARROW_UP_RIGHT}
                            />
                            <GeneralBlockAttribute
                                label={formatMessage(membershipMessages.maxAllowedSpaces)}
                                value={data?.max_allowed_spaces_count}
                            />
                            <GeneralBlockAttribute
                                label={formatMessage(membershipMessages.maxExtraVisitors)}
                                value={membershipTypeData?.max_extra_visitors_count}
                            />
                        </div>
                        <div className="flex flex-col gap-4">
                            <GeneralBlockAttribute
                                label={formatMessage(membershipMessages.validFrom)}
                                value={getFormattedDateTimeForDetailPanel(data?.valid_from)}
                            />
                            <GeneralBlockAttribute
                                label={formatMessage(membershipMessages.validTo)}
                                value={getFormattedDateTimeForDetailPanel(data?.valid_to)}
                            />
                            <GeneralBlockAttribute label={formatMessage(membershipMessages.allowedDays)}>
                                <GeneralBlockValue size="small" className="flex">
                                    {data && data.membership_allowed_times.length
                                        ? Object.values(DAY_OF_WEEK).map((day: DAY_OF_WEEK) => {
                                              const allowedTime = data.membership_allowed_times.find(
                                                  (time) => time.day_of_week === day,
                                              );
                                              const weekDay = formatMessage(membershipMessages[day]);
                                              return (
                                                  <TooltipProvider key={day}>
                                                      <Tooltip>
                                                          <TooltipTrigger asChild>
                                                              <Badge
                                                                  variant="tag"
                                                                  className={cn(
                                                                      'mr-1 flex size-6 shrink-0 items-center justify-center bg-white px-0 uppercase opacity-30 last:mr-0',
                                                                      allowedTime?.day_of_week === day &&
                                                                          'font-semibold opacity-100',
                                                                  )}
                                                                  key={day}
                                                              >
                                                                  {weekDay.charAt(0)}
                                                              </Badge>
                                                          </TooltipTrigger>
                                                          <TooltipContent side="bottom" className="flex flex-col gap-2">
                                                              <span className="text-gray-500">{weekDay}</span>
                                                              {allowedTime
                                                                  ? `${formatTime(allowedTime.from_time)} - ${formatTime(allowedTime.to_time)}`
                                                                  : formatMessage(membershipMessages.notAllowed)}
                                                          </TooltipContent>
                                                      </Tooltip>
                                                  </TooltipProvider>
                                              );
                                          })
                                        : '-'}
                                </GeneralBlockValue>
                            </GeneralBlockAttribute>
                        </div>
                    </GeneralBlock>
                </div>
                <div className="flex gap-9">
                    <GeneralBlock className="flex max-h-max flex-1 flex-col">
                        <GeneralBlockAttribute label={formatMessage(membershipMessages.template)}>
                            {data ? (
                                <GeneralBlockValue size="small">
                                    <LinkWithIcon
                                        to={`/${PATH.ADMINISTRATION}/membership-templates/${data?.membership_type.id}`}
                                        linkTitle={translate(data?.membership_type.translations)}
                                        hasPermissions={accessMembershipTemplatePermission}
                                    />
                                </GeneralBlockValue>
                            ) : (
                                '-'
                            )}
                            {categoryBadgeOption && (
                                <Badge theme={categoryBadgeOption.theme}>
                                    <FontAwesomeIcon icon={categoryBadgeOption.icon!} />
                                    {formatMessage(categoryBadgeOption.label)}
                                </Badge>
                            )}
                        </GeneralBlockAttribute>
                        <GeneralBlockAttribute label={formatMessage(membershipMessages.pricedPerSpace)}>
                            <BooleanBadge booleanField={data?.membership_type.apply_pricing_per_space} />
                        </GeneralBlockAttribute>
                        <GeneralBlockAttribute label={formatMessage(membershipMessages.autoRenew)}>
                            <BooleanBadge booleanField={data?.membership_type.auto_renew} />
                        </GeneralBlockAttribute>
                        <GeneralBlockAttribute label={formatMessage(membershipMessages.limitDuration)}>
                            <BooleanBadge booleanField={data?.membership_type.limit_duration} />
                        </GeneralBlockAttribute>
                        <GeneralBlockAttribute label={formatMessage(membershipMessages.customDuration)}>
                            <BooleanBadge booleanField={customDuration} />
                        </GeneralBlockAttribute>
                        <GeneralBlockAttribute label={formatMessage(membershipMessages.duration)} value={duration} />
                        <GeneralBlockAttribute label={formatMessage(membershipMessages.limitDuration)}>
                            <BooleanBadge booleanField={data?.membership_type.limit_duration} />
                        </GeneralBlockAttribute>
                        <GeneralBlockAttribute label={formatMessage(membershipMessages.accessOverCapacityLimit)}>
                            <BooleanBadge booleanField={data?.membership_type.allow_access_over_capacity_limit} />
                        </GeneralBlockAttribute>
                        <GeneralBlockAttribute label={formatMessage(membershipMessages.accessClientOverRestriction)}>
                            <BooleanBadge
                                booleanField={data?.membership_type.allow_access_as_client_outside_restrictions}
                            />
                        </GeneralBlockAttribute>
                    </GeneralBlock>

                    <MembershipDashboardTables membershipTypeData={membershipTypeData} templateId={templateId} />
                </div>
            </div>
        </>
    );
}
